// Live's project breadcrumbs (`project-breadcrumbs`, spec A3b, design-ref/folders-410/03-04):
// each crumb opens a menu of its siblings (the top level's with "All assets"), the arrow after the
// current folder lists its subprojects, and a path deeper than two shows the top folder, an arrow
// with its subprojects, "…" with the rest of the path as a small tree, then the current folder.
import { ActionIcon, Menu, Popover, UnstyledButton } from "@mantine/core";
import { ChevronRight, LayoutGrid, MoreHorizontal } from "lucide-react";
import { useMemo, useState } from "react";

import type { Folder } from "../../api/generated/model";
import { folderPath, folderTree, type FolderTree } from "./folder-tree";
import { FolderTreeList } from "./FolderTreeList";

export interface FolderCrumbsProps {
  folders: readonly Folder[];
  /** The library's folder; `undefined` is "All Assets". */
  folderId: string | undefined;
  onChoose: (folderId: string | undefined) => void;
  /** The crumbs' own classes, so they read as the header's other crumbs. */
  crumbClass: string;
  separatorClass: string;
}

/** A crumb whose label opens a menu of the folders beside it (live's ArrowMenu with a label). */
function CrumbMenu(props: {
  label: string;
  items: readonly Folder[];
  withAll: boolean;
  activeId: string | undefined;
  current: boolean;
  tree: FolderTree;
  onChoose: (folderId: string | undefined) => void;
  crumbClass: string;
}) {
  return (
    <Menu position="bottom-start" withinPortal>
      <Menu.Target>
        <UnstyledButton
          className={props.crumbClass}
          aria-current={props.current ? "page" : undefined}
          aria-label={`${props.label}, choose another project`}
        >
          {props.label}
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {props.withAll ? (
          <Menu.Item
            leftSection={<LayoutGrid size={14} aria-hidden />}
            data-active={props.activeId === undefined || undefined}
            onClick={() => props.onChoose(undefined)}
          >
            All assets
          </Menu.Item>
        ) : null}
        {props.items.map((item) => (
          <Menu.Item
            key={item.id}
            data-active={item.id === props.activeId || undefined}
            leftSection={
              props.tree.children(item.id).length > 0 ? <ChevronRight size={14} aria-hidden /> : null
            }
            onClick={() => props.onChoose(item.id)}
          >
            {item.name}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

/** The arrow between crumbs that lists a folder's subprojects (live's ArrowMenu, no label). */
function ArrowMenu(props: {
  label: string;
  items: readonly Folder[];
  activeId: string | undefined;
  onChoose: (folderId: string) => void;
  separatorClass: string;
}) {
  return (
    <Menu position="bottom-start" withinPortal>
      <Menu.Target>
        <ActionIcon variant="subtle" size="sm" aria-label={props.label}>
          <ChevronRight size={18} strokeWidth={1.5} className={props.separatorClass} aria-hidden />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {props.items.map((item) => (
          <Menu.Item
            key={item.id}
            data-active={item.id === props.activeId || undefined}
            onClick={() => props.onChoose(item.id)}
          >
            {item.name}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

export function FolderCrumbs(props: FolderCrumbsProps) {
  const { folders, folderId, onChoose, crumbClass, separatorClass } = props;
  const tree = useMemo(() => folderTree(folders), [folders]);
  const path = folderPath(tree, folderId);
  const [collapsedOpen, setCollapsedOpen] = useState(false);
  const roots = tree.children(null);
  const separator = <ChevronRight size={18} strokeWidth={1.5} className={separatorClass} aria-hidden />;
  const subprojects = (folder: Folder) => {
    const children = tree.children(folder.id);
    return children.length === 0 ? null : (
      <ArrowMenu
        label={`Subprojects of ${folder.name}`}
        items={children}
        activeId={undefined}
        onChoose={onChoose}
        separatorClass={separatorClass}
      />
    );
  };

  const last = path.at(-1);
  if (last === undefined) {
    return (
      <CrumbMenu
        label="All Assets"
        items={roots}
        withAll
        activeId={undefined}
        current
        tree={tree}
        onChoose={onChoose}
        crumbClass={crumbClass}
      />
    );
  }
  if (path.length <= 2) {
    return (
      <>
        {path.map((folder, index) => {
          const parent = path[index - 1];
          return (
            <span key={folder.id} style={{ display: "contents" }}>
              {index > 0 ? separator : null}
              <CrumbMenu
                label={folder.name}
                items={parent === undefined ? roots : tree.children(parent.id)}
                withAll={index === 0}
                activeId={folder.id}
                current={folder.id === last.id}
                tree={tree}
                onChoose={onChoose}
                crumbClass={crumbClass}
              />
            </span>
          );
        })}
        {subprojects(last)}
      </>
    );
  }
  const [top, second] = path;
  if (top === undefined || second === undefined) return null;
  // Live's CollapsedCrumbs: the path below the top folder, one folder per level.
  const branch = folderTree(
    path.slice(1).map((folder, index) => (index === 0 ? { ...folder, parent_id: null } : folder)),
  );
  return (
    <>
      <CrumbMenu
        label={top.name}
        items={roots}
        withAll
        activeId={top.id}
        current={false}
        tree={tree}
        onChoose={onChoose}
        crumbClass={crumbClass}
      />
      <ArrowMenu
        label={`Subprojects of ${top.name}`}
        items={tree.children(top.id)}
        activeId={second.id}
        onChoose={onChoose}
        separatorClass={separatorClass}
      />
      <Popover opened={collapsedOpen} onChange={setCollapsedOpen} position="bottom-start" shadow="md">
        <Popover.Target>
          <ActionIcon
            variant="subtle"
            size="sm"
            aria-label="The rest of the path"
            onClick={() => setCollapsedOpen(!collapsedOpen)}
          >
            <MoreHorizontal size={16} aria-hidden />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown p={4}>
          <FolderTreeList
            tree={branch}
            selectedId={last.id}
            onSelect={(folder) => {
              setCollapsedOpen(false);
              onChoose(folder.id);
            }}
            label="The rest of the path"
          />
        </Popover.Dropdown>
      </Popover>
      <span className={crumbClass} aria-current="page">
        {last.name}
      </span>
      {subprojects(last)}
    </>
  );
}
